import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { FieldPath, useForm } from 'react-hook-form';

import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import handleErrors from '../../api/handleErrors';
import addPaymentMethod, {
  AddPaymentDetails,
} from '../../api/paymentCentral/addPaymentMethod';
import { addToWallet, AddToWalletArgs } from '../../api/wallet/addToWallet';
import { DeleteIconSmall } from '../../assets/icons/Delete';
import Button from '../../components/styledComponents/Button';
import { LinkWithNoRouter } from '../../components/styledComponents/Link';
import colors from '../../constants/colors';
import { PaymentType } from '../../constants/enums';
import { paymentTermsAndConditions } from '../../constants/paymentMessages';
import paymentTypeMap, {
  getPaymentCommonName,
} from '../../constants/paymentTypeNames';
import useAnalytics from '../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../store/SnackbarProvider';
import { usePaymentsContext } from '../../store/payments';
import { setSelectedPaymentMethod } from '../../store/paymentsReducer';
import AutoPayInfoBar from '../AutoPay/components/AutoPayInfoBar';
import { SubmitProps } from './CardPaymentTypeForm';
import AccountForm from './forms/AccountForm';
import {
  accountPaymentTypeValidationMessages,
  accountPaymentTypeValidationSchema,
  AccountTypeFormValues,
} from './validation';

interface AccountPaymentTypeFormProps {
  accountType: PaymentType;
  cancelAddPaymentMethod: () => void;
  removeFromWallet?: () => void;
  formValues?: AccountTypeFormValues;
  buttonText?: string;
  onSubmit: (res?: SubmitProps) => void;
  paymentAccountToken?: string;
  showNicknameField?: boolean;
  showSaveToWalletCheckbox?: boolean;
  savePaymentToWallet?: boolean;
  showTermsAndConditions?: boolean;
  isAutoPay?: boolean;
  isLoading?: boolean;
}

const AccountPaymentTypeForm = ({
  accountType,
  cancelAddPaymentMethod,
  removeFromWallet,
  formValues,
  buttonText,
  onSubmit,
  paymentAccountToken,
  showNicknameField,
  showSaveToWalletCheckbox,
  savePaymentToWallet,
  showTermsAndConditions = false,
  isAutoPay = false,
  isLoading = false,
}: AccountPaymentTypeFormProps) => {
  const initialValues: AccountTypeFormValues = formValues ?? {
    name: '',
    accountNumber: '',
    routingNumber: '',
    nickname: '',
  };

  const {
    control,
    formState: { isValid, isSubmitting },
    setValue,
    handleSubmit,
    setError,
  } = useForm<AccountTypeFormValues>({
    defaultValues: initialValues,
    mode: 'onTouched',
    resolver: yupResolver(accountPaymentTypeValidationSchema(!!formValues)),
  });
  const { track } = useAnalytics();
  const { dispatch } = usePaymentsContext();
  const queryClient = useQueryClient();
  const { data } = useWelcomeQuery();
  const { enqueueSnackbar, closeSnackbar } = useSnackbarsContext();
  const getPaymentTokenFromPaymentCentral = useMutation({
    mutationFn: (args: AddPaymentDetails) => addPaymentMethod(args),
  });
  const addPaymentToWallet = useMutation({
    mutationFn: (args: AddToWalletArgs) => addToWallet(args),
    onSuccess: async () => {
      // invalidate the wallet data in the cache so it is refetched after the POST completes
      await queryClient.invalidateQueries({
        queryKey: ['wallet'],
      });
    },
  });
  const authToken = data?.authToken ?? '';
  const custKey = data?.userInfo?.custKey ?? '';

  const [rNumber, setRNumber] = useState('');
  const [aNumber, setANumber] = useState('');
  const [saveToWalletSelected, setSaveToWalletSelected] = useState(false);

  const handleFormSubmit = async (
    values: AccountTypeFormValues
  ): Promise<void> => {
    closeSnackbar();

    const isChecking = accountType === PaymentType.CHECKING;
    // Submit payment method to PaymentCentral and get a payment token
    const response = await getPaymentTokenFromPaymentCentral.mutateAsync({
      authToken,
      ...(!formValues && { accountNumber: aNumber, routingNumber: rNumber }),
      ...(paymentAccountToken && { paymentAccountToken }),
      name: values.name,
      accountType: isChecking ? 'CHKG' : 'SAVG',
    });

    if ('paymentAccountToken' in response) {
      const nicknameUpdated =
        formValues && values.nickname !== formValues.nickname;
      track('Payment Methods Submitted', {
        event_type: 'User Information Entered',
        location: 'Feature Card',
        account_type: isChecking ? 'Checking Account' : 'Savings Account',
        status: 'Succeeded',
      });

      // nickname must be updated in the wallet, not payment central
      if (saveToWalletSelected || savePaymentToWallet || nicknameUpdated) {
        const accountObj = {
          paymentAccountToken: response.paymentAccountToken,
          shortName: values.nickname ?? '',
          isPreferred: 'false',
        };

        await addPaymentToWallet.mutateAsync({
          account: accountObj,
          ownerId: custKey,
        });
      }

      const lastFour = values.accountNumber.includes(String.fromCharCode(8226))
        ? values.accountNumber.replaceAll(String.fromCharCode(8226), '')
        : values.accountNumber.slice(values.accountNumber.length - 4);

      dispatch(
        setSelectedPaymentMethod({
          analyticsAccountType: getPaymentCommonName(
            paymentTypeMap[accountType]
          ),
          name: values.name,
          paymentToken: response.paymentAccountToken,
          paymentMethod: response.paymentAcctFopType,
          displayPaymentMethod: `${paymentTypeMap[accountType]} ****${lastFour}`,
        })
      );
      if (isAutoPay) {
        onSubmit({
          paymentAccountToken: response.paymentAccountToken,
          paymentAcctFopType: response.paymentAcctFopType,
        });
      } else {
        onSubmit({
          paymentType: accountType,
          lastFour,
        });
      }
    } else if ('error' in response) {
      track('Payment Methods Submitted', {
        event_type: 'User Information Entered',
        location: 'Feature Card',
        account_type: isChecking ? 'Checking Account' : 'Savings Account',
        status: 'Failed',
      });
      const fieldWithError = handleErrors(response.error.errorMessage);

      if (fieldWithError) {
        setError(fieldWithError as FieldPath<AccountTypeFormValues>, {
          message: accountPaymentTypeValidationMessages[fieldWithError],
        });
      } else {
        enqueueSnackbar({
          dataTestId: 'accountPaymentTypeErrorSnackBar',
          title: 'We are unable to process your request right now.',
          content: (
            <>
              Check the details and try again, try another payment method or
              call Service at{' '}
              <Link
                href="tel:+8889805650"
                underline="none"
                sx={{
                  fontSize: {
                    mobile: 16,
                    tablet: 18,
                  },
                  fontWeight: 700,
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  color: colors.csaaSpectrumBlue50,
                }}
              >
                888.980.5650
              </Link>
              .
            </>
          ),
          variant: 'error',
        });
      }
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AccountForm
            control={control}
            showNicknameField={showNicknameField}
            setANumber={setANumber}
            setRNumber={setRNumber}
            setRhfValue={setValue}
            editing={!!formValues}
          />
          {isAutoPay && (
            <Stack sx={{ pb: 3 }}>
              <AutoPayInfoBar addingCardDetails={!formValues} />
            </Stack>
          )}
          <Stack
            direction={{ mobile: 'column', tablet: 'row' }}
            justifyContent={
              showSaveToWalletCheckbox ? 'space-between' : 'flex-end'
            }
            spacing={{ mobile: 1, tablet: 0 }}
          >
            {showSaveToWalletCheckbox && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={saveToWalletSelected}
                    size="small"
                    sx={{
                      [`&, &.${checkboxClasses.checked}`]: {
                        color: colors.csaaBlack,
                      },
                    }}
                    onChange={() =>
                      setSaveToWalletSelected(!saveToWalletSelected)
                    }
                  />
                }
                label="Save to payment methods"
                componentsProps={{
                  typography: {
                    sx: {
                      fontSize: { mobile: 18 },
                      fontFamily: 'Cabin',
                    },
                  },
                }}
              />
            )}
            <Stack direction={{ mobile: 'column', tablet: 'row' }} spacing={1}>
              {removeFromWallet && (
                <Stack
                  direction="row"
                  component={Button}
                  spacing={1}
                  onClick={removeFromWallet}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    mr: 2,
                    cursor: 'pointer',
                    backgroundColor: 'inherit',
                    '&:focus': {
                      outline: `solid 2px ${colors.csaaSpectrumRed50}`,
                      outlineOffset: '1px',
                      border: 'none',
                      borderRadius: 1,
                      backgroundColor: 'inherit',
                    },
                    '&:hover': {
                      backgroundColor: colors.redInteractiveButtonHover,
                    },
                  }}
                >
                  <DeleteIconSmall />
                  <Typography
                    sx={{
                      color: colors.csaaSpectrumRed50,
                      fontWeight: 700,
                      fontSize: { mobile: 18 },
                    }}
                  >
                    Remove from wallet
                  </Typography>
                </Stack>
              )}
              <Button
                variant="outlined"
                color="inherit"
                data-testid="paymentMethodSelectCancel"
                disableRipple
                sx={{
                  borderWidth: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: 18,
                  color: 'text.secondary',
                  px: 4,
                  py: 0.5,
                }}
                onClick={cancelAddPaymentMethod}
              >
                Cancel
              </Button>
              <Button
                disableRipple
                disabled={!isValid || isSubmitting}
                type="submit"
                data-testid="accountPaymentTypeFormDoneButton"
                loading={
                  isSubmitting ||
                  addPaymentToWallet.isPending ||
                  getPaymentTokenFromPaymentCentral.isPending ||
                  isLoading
                }
              >
                {buttonText}
              </Button>
            </Stack>
          </Stack>
          {showTermsAndConditions && (
            <Stack alignItems="flex-end" sx={{ pt: { mobile: 1, tablet: 3 } }}>
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontSize: 16,
                  textAlign: { mobile: 'center', tablet: 'right' },
                  letterSpacing: '0.2px',
                }}
              >
                {paymentTermsAndConditions(buttonText)}
                <LinkWithNoRouter
                  href={
                    isAutoPay
                      ? 'https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/auth-terms.html'
                      : 'https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/payment-terms.html'
                  }
                  target="_blank"
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: 16,
                    fontWeight: 700,
                  }}
                  onClick={() =>
                    track('Terms and Conditions Accessed', {
                      event_type: 'Link Accessed',
                      link: `${
                        isAutoPay ? 'AutoPay' : 'Payment'
                      } Terms and Conditions`,
                      location: 'Feature Card',
                    })
                  }
                >
                  {isAutoPay ? 'AutoPay' : 'Payment'} Terms and Conditions
                </LinkWithNoRouter>
                .
              </Typography>
            </Stack>
          )}
        </Box>
      </form>
    </>
  );
};

export default AccountPaymentTypeForm;
